import React from "react";
import { Box, Button } from "@mui/material";
import Image from "next/image";
import Typography from "@mui/material/Typography";
import price from "@/utils/price";
import IconButton from "@mui/material/IconButton";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccordionDetails from "@mui/material/AccordionDetails";
import { t, Trans } from "@lingui/macro";
import InputGrayStart from "@/components/core/input-gray-start";
import { useLingui } from "@lingui/react";
import { useQueryClient } from "@tanstack/react-query";
import { baseUrl } from "@/utils/api-definition";
import { useGiftStore } from "@/components/context/gift-store";
import ImageWithFallback from "../image-with-fallback";

interface SelectedProductProps {
  showWarning: boolean;
  setShowWarning: React.Dispatch<React.SetStateAction<boolean>>;
  isLoadingProduct: boolean;
}

const SelectedProduct: React.FC<SelectedProductProps> = ({
  showWarning,
  setShowWarning,
  isLoadingProduct,
}) => {
  const { i18n } = useLingui();
  const queryClient = useQueryClient();
  const {
    setProductId,
    product,
    hasPriceOption,
    setHasPriceOption,
    productOption,
    setProductOption,
    setErrorMessage,
    errors,
  } = useGiftStore();
  const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>, option: any) => {
    let errorMessage = "";
    const inputValue = event.target.value;
    if (!/^[0-9]*\.?[0-9]*$/.test(inputValue)) {
      errorMessage = t(i18n)`Invalid input! Please enter a positive number.`;
    } else if (inputValue === "") {
      errorMessage = t(i18n)`Price required!`;
    } else if (Number(inputValue) < option.conditions.min_amount) {
      errorMessage =
        t(i18n)`Price can not be below` + ": " + price(Number(option.conditions.min_amount));
    } else if (Number(inputValue) > option.conditions.max_amount) {
      errorMessage =
        t(i18n)`Price can not be over` + ": " + price(Number(option.conditions.max_amount));
    }

    setShowWarning(false);
    setErrorMessage(String(option.option_id), errorMessage);

    const updatedArray = [...productOption];
    const priceOption = updatedArray.find(
      (element: { key: string; value: string }) => element.key == hasPriceOption.product_option_id,
    );
    if (!priceOption) {
      updatedArray.push({ key: hasPriceOption.product_option_id, value: inputValue });
    } else {
      priceOption.value = inputValue;
    }

    setProductOption(updatedArray);
  };
  const removeProduct = () => {
    setProductId("");
    queryClient.invalidateQueries({ queryKey: ["product"] });
    setProductOption([]);
    setHasPriceOption({ product_option_id: "", option_id: "" });
    setErrorMessage("product", t(i18n)`Please select a product!`);
  };
  const productImage = `${baseUrl}/image/${baseUrl ? product?.original_image.replace(baseUrl, "").replace("image/", "") : product?.original_image}`;
  const handleClickSimilarProduct = (id: string) => {
    queryClient.invalidateQueries({ queryKey: ["product"] });
    setProductId(id);
  };
  const priceOption = productOption.find(
    (option) => hasPriceOption.product_option_id === option.key,
  );
  return (
    <>
      {isLoadingProduct && (
        <Box className="flex min-h-[200px] items-center justify-center">
          <Image src="/loader.gif" width="30" height="30" alt="Loader" loading="lazy" />
        </Box>
      )}
      {!isLoadingProduct && product?.id && (
        <Box>
          <Box
            key={product.id}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              margin: "20px 0px",
              gap: "20px",
            }}
          >
            <Box
              key={product.id}
              sx={{
                display: "flex",
                gap: "12px",
              }}
            >
              <ImageWithFallback
                loading="lazy"
                src={productImage}
                alt=""
                width={90}
                height={60}
                className="self-center"
              />
              <Box
                sx={{
                  padding: "10px 0px",
                }}
              >
                <Typography
                  sx={{
                    fontSize: "14px",
                    display: "-webkit-flex",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    WebkitLineClamp: "2",
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  {product.name}
                </Typography>
                {product.special === 0 ? (
                  <Typography variant="button" sx={{ fontSize: "14px" }}>
                    {price(product.price)}
                  </Typography>
                ) : (
                  <Typography variant="button" sx={{ color: "red", fontSize: "14px" }}>
                    {price(product.special)}
                    <Typography
                      variant="button"
                      sx={{
                        textDecoration: "line-through",
                        color: "red",
                        fontSize: "12px",
                        marginLeft: "5px",
                      }}
                    >
                      {price(product.price)}
                    </Typography>
                  </Typography>
                )}
              </Box>
            </Box>
            <IconButton
              disableRipple
              aria-label="delete"
              size="small"
              onClick={() => removeProduct()}
              sx={{
                margin: "5px 0px",
                broderRadius: "100%",
                bgcolor: "#eee",
                height: "40px",
                width: "40px",
                color: "#999",
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: "16" }} />
            </IconButton>
          </Box>
          {product.offerz_product_tabs.length > 0 && (
            <Box>
              {product.offerz_product_tabs.map((tab: any) => {
                if (tab.text)
                  return (
                    <Accordion
                      defaultExpanded={tab.view_required === 1}
                      key={tab.name}
                      sx={{
                        boxShadow: "none",
                      }}
                    >
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        aria-controls="panel1-content"
                        id="panel1-header"
                        sx={{ padding: "0px" }}
                      >
                        <Typography sx={{ fontWeight: 500 }}>{tab.name}</Typography>
                      </AccordionSummary>
                      <AccordionDetails sx={{ padding: "0px" }}>
                        <Box
                          sx={{
                            ul: {
                              listStyleType: "disc",
                              listStylePosition: "inside",
                            },
                          }}
                          dangerouslySetInnerHTML={{ __html: tab.text }}
                        />
                      </AccordionDetails>
                    </Accordion>
                  );
              })}
              <Box sx={{ margin: "0px 0 30px", borderTop: "1px solid #d2d2d7" }}></Box>
            </Box>
          )}
          {product.offerz_similar_products.length > 1 && (
            <Box>
              <Typography sx={{ fontSize: "17px", color: "#000", fontWeight: "600" }}>
                <Trans>Choose the amount</Trans>
              </Typography>
              <Box
                sx={{
                  gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
                  display: "grid",
                  padding: "20px 0px 10px",
                  gap: "5px",
                }}
              >
                {product.offerz_similar_products.map((similarProduct: any) => (
                  <Button
                    data-testid={`increments-option-${similarProduct.product_id}`}
                    key={`increments-option-${similarProduct.product_id}`}
                    onClick={() => handleClickSimilarProduct(similarProduct.product_id)}
                    sx={{
                      fontWeight: "normal",
                      maxWidth: "unset",
                      cursor: "pointer",
                      borderRadius: "8px",
                      borderWidth: "1px",
                      borderStyle: "solid",
                      padding: "11px 8px",
                      width: "100%",
                      height: "auto",
                      fontSize: "15px !important",
                      whiteSpace: "nowrap",
                      color: similarProduct.product_id == product.id ? "#000" : "rgba(0,0,0,0.6)",
                      borderColor:
                        similarProduct.product_id == product.id ? "primary.main" : "#bcbfc1",
                      backgroundColor:
                        similarProduct.product_id == product.id ? "primary.light" : "transparent",
                      "&:hover": {
                        transition: "0.3s ease",
                        borderColor: "primary.main",
                        color: "#000",
                        backgroundColor: "primary.light",
                      },
                    }}
                  >
                    <Trans> {similarProduct.title}</Trans>
                  </Button>
                ))}
              </Box>
              <Box sx={{ margin: "20px 0 20px", borderTop: "1px solid #d2d2d7" }}></Box>
            </Box>
          )}

          {hasPriceOption.product_option_id && (
            <>
              <InputGrayStart
                key={hasPriceOption.option_id}
                sxProps={{ mt: 1 }}
                placeholder={t(i18n)`Enter amount`}
                name="own"
                type="number"
                min={0}
                value={priceOption !== undefined ? priceOption.value : ""}
                onKeyDown={(event) => {
                  if (["e", "E", "+", "-"].includes(event.key)) {
                    event.preventDefault();
                  }
                }}
                onChange={(event) => handleOptionChange(event, hasPriceOption)}
                startAdornmentLabel="CHF"
                maxWidth={"100%"}
              />
              {showWarning &&
                !!errors.length &&
                errors.find((element) => element.key == hasPriceOption.option_id) && (
                  <Typography
                    sx={{ fontSize: "13px", fontWeight: 500, color: "#fd7b7c", pt: "4px" }}
                  >
                    {errors.find((element) => element.key == hasPriceOption.option_id)?.error}
                  </Typography>
                )}
              <Box sx={{ margin: "30px 0 30px", borderTop: "1px solid #d2d2d7" }}></Box>
            </>
          )}
        </Box>
      )}
    </>
  );
};

export default SelectedProduct;
